/**
 * アプリケーションナビゲーター
 * React Routerで来場者向け15画面のURLと画面遷移を定義します。
 */

import React from "react";
import VisitorFeatureGate from "../features/visitor/components/VisitorFeatureGate";
import { GuidedTourHost } from "../features/visitor/components/VisitorGuidedTour";
import {
  BrowserRouter,
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import VisitorHomeScreen from "../features/visitor/screens/VisitorHomeScreen";
import VisitorLoginScreen from "../features/visitor/screens/VisitorLoginScreen";
import VisitorRegisterScreen from "../features/visitor/screens/VisitorRegisterScreen";
import VisitorScanScreen from "../features/visitor/screens/VisitorScanScreen";
import VisitorSessionGateScreen from "../features/visitor/screens/VisitorSessionGateScreen";
import VisitorThankYouScreen from "../features/visitor/screens/VisitorThankYouScreen";
import VisitorRouteErrorBoundary from "../shared/components/VisitorRouteErrorBoundary";
import VisitorRouteLoadingFallback from "../shared/components/VisitorRouteLoadingFallback";
import usePublicAppSettings from "../shared/hooks/usePublicAppSettings";
import { ROOT_ROUTES, VISITOR_ROUTES } from "./routeNames";
import {
  useWebScreenProps,
  VISITOR_ROUTE_PATHS,
} from "./webNavigation";

/** キャンパスマップ画面を遅延読み込みする */
const VisitorCampusMapScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorCampusMapScreen"),
);

/** イベント画面を遅延読み込みする */
const VisitorEventsScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorEventsScreen"),
);

/** 友達グループ画面を遅延読み込みする */
const VisitorGroupScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorGroupScreen"),
);

/** お知らせ画面を遅延読み込みする */
const VisitorNoticesScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorNoticesScreen"),
);

/** ptランキング画面を遅延読み込みする */
const VisitorRankingScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorRankingScreen"),
);

/** 交換QR画面を遅延読み込みする */
const VisitorRedeemQrScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorRedeemQrScreen"),
);

/** 交換履歴画面を遅延読み込みする */
const VisitorRedemptionsScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorRedemptionsScreen"),
);

/** 景品画面を遅延読み込みする */
const VisitorRewardsScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorRewardsScreen"),
);

/** 屋台評価画面を遅延読み込みする */
const VisitorStallRatingScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorStallRatingScreen"),
);

/** アンケート画面を遅延読み込みする */
const VisitorSurveysScreen = React.lazy(
  () => import("../features/visitor/screens/VisitorSurveysScreen"),
);

/** 画面コンポーネント契約 */
type VisitorScreenComponent = React.ComponentType<{
  navigation: Record<string, any>;
  route: Record<string, any>;
}>;

/**
 * 既存画面へWebナビゲーション互換プロパティを渡す
 * @param {{component: VisitorScreenComponent, routeName: string}} props - 画面設定
 * @returns {JSX.Element} 画面
 */
const WebScreen = ({
  component: ScreenComponent,
  routeName,
}: {
  component: VisitorScreenComponent;
  routeName: string;
}) => {
  const screenProps = useWebScreenProps(routeName);
  return <VisitorFeatureGate routeName={routeName} navigation={screenProps.navigation}>
    <ScreenComponent {...screenProps} />
  </VisitorFeatureGate>;
};

/** Webルート定義 */
export const WEB_ROUTE_CONFIGS = Object.freeze([
  {
    routeName: VISITOR_ROUTES.ENTRY,
    component: VisitorSessionGateScreen,
  },
  { routeName: VISITOR_ROUTES.LOGIN, component: VisitorLoginScreen },
  { routeName: VISITOR_ROUTES.REGISTER, component: VisitorRegisterScreen },
  { routeName: VISITOR_ROUTES.HOME, component: VisitorHomeScreen },
  { routeName: VISITOR_ROUTES.NOTICES, component: VisitorNoticesScreen },
  { routeName: VISITOR_ROUTES.EVENTS, component: VisitorEventsScreen },
  { routeName: VISITOR_ROUTES.SCAN, component: VisitorScanScreen },
  { routeName: VISITOR_ROUTES.REWARDS, component: VisitorRewardsScreen },
  { routeName: VISITOR_ROUTES.REDEEM_QR, component: VisitorRedeemQrScreen },
  {
    routeName: VISITOR_ROUTES.REDEMPTIONS,
    component: VisitorRedemptionsScreen,
  },
  { routeName: VISITOR_ROUTES.SURVEYS, component: VisitorSurveysScreen },
  {
    routeName: VISITOR_ROUTES.STALL_RATING,
    component: VisitorStallRatingScreen,
  },
  { routeName: VISITOR_ROUTES.RANKING, component: VisitorRankingScreen },
  { routeName: VISITOR_ROUTES.GROUP, component: VisitorGroupScreen },
  {
    routeName: VISITOR_ROUTES.CAMPUS_MAP,
    component: VisitorCampusMapScreen,
  },
]);

/**
 * サンクスページの表示中も通常どおり開ける画面。
 * 開催終了の時刻に窓口へ並んでいる来場者の交換QRを消さないため、景品交換の画面を残す。
 * 交換QRは景品画面と交換QR画面のカートから出る。交換履歴は景品画面から1タップで開くため一緒に残す。
 */
const THANK_YOU_CONTINUED_ROUTES = Object.freeze([
  VISITOR_ROUTES.REWARDS,
  VISITOR_ROUTES.REDEEM_QR,
  VISITOR_ROUTES.REDEMPTIONS,
]);

/**
 * サンクスページの表示中も通常どおり開ける画面のURLか判定する
 * @param {string} pathname - 表示中のURLのパス
 * @returns {boolean} 通常どおり開けるならtrue
 */
const isThankYouContinuedPath = (pathname: string): boolean =>
  THANK_YOU_CONTINUED_ROUTES.some((routeName) =>
    matchPath(VISITOR_ROUTE_PATHS[routeName], pathname),
  );

/**
 * 初期URLを正規化する
 * @param {string} initialPath - 指定パス
 * @returns {string | null} 正規化済みURL
 */
const normalizeInitialUrl = (initialPath: string) => {
  const normalizedPath =
    typeof initialPath === "string" ? initialPath.trim() : "";
  if (!normalizedPath) {
    return null;
  }
  const pathWithLeadingSlash = normalizedPath.startsWith("/")
    ? normalizedPath
    : `/${normalizedPath}`;
  return `https://ikomasai.local${pathWithLeadingSlash}`;
};

/**
 * 互換用の公開URL設定を返す
 * @param {string} initialPath - テスト用初期パス
 * @returns {Object} URL設定
 */
export const createLinkingOptions = (initialPath = "") => ({
  prefixes: [
    String(process.env.VITE_PUBLIC_APP_BASE_URL ?? "")
      .trim()
      .replace(/\/+$/, ""),
    typeof window !== "undefined" ? window.location.origin : "",
    "https://ikomasai.local",
  ].filter(
    (prefix, index, prefixes) =>
      prefix && prefixes.indexOf(prefix) === index,
  ),
  config: {
    screens: {
      [ROOT_ROUTES.VISITOR_FLOW]: {
        path: "",
        screens: Object.fromEntries(
          Object.entries(VISITOR_ROUTE_PATHS).map(([routeName, path]) => [
            routeName,
            path === "/" ? "" : path.slice(1),
          ]),
        ),
      },
    },
  },
  /**
   * 初期URLを取得する
   * @returns {Promise<string | null>} 初期URL
   */
  async getInitialURL() {
    return (
      normalizeInitialUrl(initialPath) ??
      (typeof window === "undefined" ? null : window.location.href)
    );
  },
});

/**
 * 来場者ルート群
 * 遅延読込画面のchunk取得失敗や描画例外で全体が空白にならないよう、エラー境界で包む。
 * 使い方ガイドはエラー境界・Suspenseの外（兄弟）に置き、画面の切替・遅延読み込み・画面の例外で消えないようにする。
 * サンクスページの判定はサーバーの確定値だけを見る。取得中・取得失敗は既定値のfalseなので通常画面を出し、起動を遅らせない。
 * 使い方ガイドは次の手順で閉じた画面へ案内してしまうため、サンクスページの表示中は残した画面でも出さない。
 * @returns {JSX.Element} ルート定義
 */
const VisitorRoutes = () => {
  /** 現在のURL */
  const location = useLocation();
  /** 公開設定 */
  const { settings } = usePublicAppSettings();

  return (
    <>
      <VisitorRouteErrorBoundary
        homePath={VISITOR_ROUTE_PATHS[VISITOR_ROUTES.HOME]}
        resetKey={location.pathname}
      >
        <React.Suspense fallback={<VisitorRouteLoadingFallback />}>
          {settings.thankYouPageActive && !isThankYouContinuedPath(location.pathname) ? (
            <VisitorThankYouScreen />
          ) : (
            <Routes>
              {WEB_ROUTE_CONFIGS.map(({ routeName, component }) => (
                <Route
                  element={
                    <WebScreen component={component} routeName={routeName} />
                  }
                  key={routeName}
                  path={VISITOR_ROUTE_PATHS[routeName]}
                />
              ))}
              <Route element={<Navigate replace to="/" />} path="*" />
            </Routes>
          )}
        </React.Suspense>
      </VisitorRouteErrorBoundary>
      {settings.thankYouPageActive ? null : <GuidedTourHost />}
    </>
  );
};

/**
 * アプリケーションナビゲーター
 * @param {{initialPath?: string}} props - テスト用初期パス
 * @returns {JSX.Element} ルーター
 */
const AppNavigator = ({ initialPath = "" }: { initialPath?: string }) => {
  const basename = "";

  if (
    initialPath &&
    typeof window !== "undefined" &&
    window.location.pathname === "/"
  ) {
    const normalizedPath = initialPath.startsWith("/")
      ? initialPath
      : `/${initialPath}`;
    window.history.replaceState(null, "", normalizedPath);
  }

  return (
    <BrowserRouter basename={basename}>
      <VisitorRoutes />
    </BrowserRouter>
  );
};

export default AppNavigator;
